/**
 * Pure positional-data model for a Pages container's page assignments and
 * shared children.
 *
 * `PagesData.pages` is positionally aligned with the PAGE children (the
 * container's direct children minus resolved `shared` entries), and `shared`
 * holds container-relative paths of non-page children. Every structural page
 * action (reorder, delete, duplicate, add) must keep `pages[]` aligned and
 * every rename must keep the stored paths current (the reference-identity
 * registry heals them too, but asynchronously — rewriting in the same commit
 * closes the stale window).
 *
 * All functions are pure: they take data in, return new data out (or
 * `undefined` when the result would be empty, matching the serialization
 * convention that trailing/empty structures are omitted). `splitPageChildren`
 * takes scene access as `resolve`, so the module is unit-testable without
 * Babylon.
 */

import type { SceneObject } from '../../core/SceneObject';
import { PAGE_STATE_EXIT, type PagesData, type PageData, type StateAssignment } from '../../scenedata/behaviors/pagesdata';

/** Split a container's direct children into page children and shared children.
 *  Unresolvable shared paths are returned separately (the child they pointed
 *  at stays a page — same fail-safe as the runtime). */
export function splitPageChildren(
    children: readonly SceneObject[],
    shared: readonly string[] | undefined,
    resolve: (path: string) => SceneObject | null,
): { pageChildren: SceneObject[]; sharedChildren: SceneObject[]; unresolvedShared: string[] } {
    if (!shared || shared.length === 0) {
        return { pageChildren: [...children], sharedChildren: [], unresolvedShared: [] };
    }
    const resolved = new Set<SceneObject>();
    const unresolvedShared: string[] = [];
    for (const path of shared) {
        const obj = resolve(path);
        if (obj) resolved.add(obj);
        else unresolvedShared.push(path);
    }
    return {
        pageChildren: children.filter(c => !resolved.has(c)),
        sharedChildren: children.filter(c => resolved.has(c)),
        unresolvedShared,
    };
}

/** Pad `pages` to `pageCount` entries ({} for missing) so positional surgery
 *  is safe, then let the caller splice. */
function padded(pages: readonly PageData[] | undefined, pageCount: number): PageData[] {
    const out: PageData[] = [];
    for (let i = 0; i < pageCount; i++) out.push(pages?.[i] ?? {});
    return out;
}

/** Drop trailing entries with no content, per the serialization convention. */
function trimmed(pages: PageData[]): PageData[] | undefined {
    while (pages.length > 0 && !pages[pages.length - 1].states) pages.pop();
    return pages.length > 0 ? pages : undefined;
}

/** Remap `pages` after a reorder. `indexMap[newPos] = oldPos`, computed over
 *  PAGE indices only (shared children are not part of the ordering). */
export function remapPagesForReorder(
    pages: readonly PageData[] | undefined,
    indexMap: readonly number[],
    pageCount: number,
): PageData[] | undefined {
    if (!pages || pages.length === 0) return undefined;
    const before = padded(pages, pageCount);
    return trimmed(indexMap.map(oldIndex => before[oldIndex] ?? {}));
}

/** Splice out the deleted page's entry so later pages keep their assignments. */
export function spliceForDelete(
    pages: readonly PageData[] | undefined,
    index: number,
    pageCount: number,
): PageData[] | undefined {
    if (!pages || pages.length === 0) return undefined;
    const next = padded(pages, pageCount);
    next.splice(index, 1);
    return trimmed(next);
}

/** Rewrite a container-relative path when the node at `from` moves to `to`: `from` itself and every path under it. */
function retargetPath(path: string, from: string, to: string): string {
    if (path === from) return to;
    if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
    return path;
}

/** After duplicating page `srcIndex` (named `srcName`) as a new LAST page named
 *  `newName`: append a deep clone of the source entry with its assignment
 *  targets retargeted from the source page's subtree to the duplicate's.
 *  Targets pointing elsewhere (shared children, cross-page patterns) carry
 *  over verbatim. `pageCount` is the count BEFORE the duplicate was added. */
export function cloneEntryForDuplicate(
    pages: readonly PageData[] | undefined,
    srcIndex: number,
    srcName: string,
    newName: string,
    pageCount: number,
): PageData[] | undefined {
    const src = pages?.[srcIndex];
    if (!src?.states || src.states.length === 0) return pages ? trimmed(padded(pages, pageCount)) : undefined;
    const next = padded(pages, pageCount);
    const cloned: StateAssignment[] = src.states.map(a => ({
        ...a,
        target: retargetPath(a.target, `./${srcName}`, `./${newName}`),
    }));
    next.push({ states: cloned });
    return trimmed(next);
}

/** Set (state != null) or remove (state == null) the assignment for `target`
 *  on page `pageIndex`. One assignment per target per page. */
export function setAssignmentForTarget(
    pages: readonly PageData[] | undefined,
    pageIndex: number,
    target: string,
    state: string | null,
    pageCount: number,
): PageData[] | undefined {
    const next = padded(pages, pageCount);
    const entry = next[pageIndex] ?? {};
    const kept = (entry.states ?? []).filter(a => a.target !== target);
    if (state !== null) kept.push({ target, state });
    next[pageIndex] = kept.length > 0 ? { ...entry, states: kept } : { ...entry, states: undefined };
    return trimmed(next);
}

/** Rewrite every stored path (shared entries + all assignment targets) for a
 *  rename of a direct child, in one pass — the same-commit hardening that
 *  makes the async heal a no-op. Returns the (possibly identical) data. */
export function retargetDataForRename(
    data: PagesData,
    oldName: string,
    newName: string,
): PagesData {
    return retargetDataForMove(data, `./${oldName}`, `./${newName}`);
}

/**
 * {@link retargetDataForRename} for a node at any depth: every stored path at
 * or under the container-relative `from` moves to `to`. Returns the (possibly
 * identical) data. Internal to `editor/pages`.
 */
export function retargetDataForMove(data: PagesData, from: string, to: string): PagesData {
    let changed = false;
    const shared = data.shared?.map(p => {
        const next = retargetPath(p, from, to);
        if (next !== p) changed = true;
        return next;
    });
    const pages = data.pages?.map(page => {
        if (!page?.states) return page;
        let pageChanged = false;
        const states = page.states.map(a => {
            const next = retargetPath(a.target, from, to);
            if (next !== a.target) pageChanged = true;
            return next === a.target ? a : { ...a, target: next };
        });
        if (pageChanged) changed = true;
        return pageChanged ? { ...page, states } : page;
    });
    return changed ? { ...data, shared, pages } : data;
}

/**
 * `pages` without any assignment targeting `path` or a node under it, trailing
 * empty entries left out. Internal to `editor/pages`: not exported from
 * `trace-viewer/editor`.
 */
export function withoutTarget(pages: readonly PageData[] | undefined, path: string): PageData[] | undefined {
    if (!pages) return undefined;
    return trimmed(pages.map(page => {
        const states = page?.states?.filter(a => a.target !== path && !a.target.startsWith(`${path}/`));
        return states && states.length > 0 ? { ...page, states } : { ...page, states: undefined };
    }));
}

/** `items` with the one at `from` moved into the gap `slot` (0..length) names. Internal to `editor/pages`. */
export function moveItem<T>(items: readonly T[], from: number, slot: number): T[] {
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(slot > from ? slot - 1 : slot, 0, moved);
    return next;
}

/** The page a container-relative `target` sits on: the page its first segment names, or -1. */
function homePage(target: string, pageNames: readonly string[]): number {
    return pageNames.indexOf(target.replace(/^\.\//, '').split('/')[0]);
}

/**
 * `pages` with a transition preset baked for `target`, or cleared with
 * `on` false: every page but the one `target` sits on assigns it `_pageExit`,
 * and its own page assigns nothing, so it shows Base there. Internal to
 * `editor/pages`.
 */
export function withPresetAssignments(
    pages: readonly PageData[] | undefined,
    target: string,
    on: boolean,
    pageNames: readonly string[],
): PageData[] | undefined {
    const home = homePage(target, pageNames);
    return trimmed(padded(pages, Math.max(pages?.length ?? 0, pageNames.length)).map((page, i) => {
        const states = (page.states ?? []).filter(a => a.target !== target);
        if (on && i < pageNames.length && i !== home) states.push({ target, state: PAGE_STATE_EXIT });
        return { ...page, states: states.length > 0 ? states : undefined };
    }));
}

/**
 * `pages` after a page was added at `newIndex` to a deck with a baked preset,
 * so the new page takes part: each target already assigned `_pageExit`
 * somewhere, and each of `presetTargets`, exits on every page but its own. A
 * target on no page (shared content) is only assigned on the new page. A page
 * that assigns a target another state keeps it. Internal to `editor/pages`.
 */
export function foldNewPageIntoExitAssignments(
    pages: readonly PageData[] | undefined,
    pageNames: readonly string[],
    newIndex: number,
    presetTargets: readonly string[],
): PageData[] | undefined {
    const participants = new Set(presetTargets);
    for (const page of pages ?? []) {
        for (const a of page?.states ?? []) if (a.state === PAGE_STATE_EXIT) participants.add(a.target);
    }
    const next = padded(pages, pageNames.length).map(page => ({ ...page, states: page.states?.map(a => ({ ...a })) }));
    for (const target of participants) {
        const home = homePage(target, pageNames);
        if (home >= 0) {
            const kept = next[home].states?.filter(a => !(a.target === target && a.state === PAGE_STATE_EXIT));
            next[home].states = kept && kept.length > 0 ? kept : undefined;
        }
        const onto = home >= 0 ? next.map((_, i) => i).filter(i => i !== home) : [newIndex];
        for (const i of onto) {
            const states = next[i].states ?? [];
            if (!states.some(a => a.target === target)) next[i].states = [...states, { target, state: PAGE_STATE_EXIT }];
        }
    }
    return trimmed(next);
}
